import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import HomeTableHeaderCell from 'components/layout/HomeTableHeaderCell';
import LoaderComponent from 'components/LoaderComponent';
import { PatientConsumerRenderProps } from 'components/patient/PatientContext';
import { TreatmentMode, TreatmentsConsumerRenderProps } from 'components/treatments/TreatmentsContext';
import TreatmentsForm from 'components/treatments/TreatmentsForm';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Icon, Table } from 'semantic-ui-react';
import { getAllMandates } from 'services/mandateServices';
import { getAllTreatmentsByPatientId } from 'services/treatmentServices';
import styled from 'styled-components';
import { BillStatus } from 'ts-types/api.enums';
import { ConsultationDto, MandateDto, TreatmentDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const TreatmentsInfoContainer = styled.div`
  flex-grow: 1;
  cursor: context-menu;
  overflow: auto;
  max-height: 321px;

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  .date-class {
    display: inline-flex;
    padding-right: 5px;
  }

  .icon-class {
    display: inline-flex;
  }

  & .ui.table tbody tr.deleted-treatment {
    color: red;

    &:hover {
      color: red !important;
    }
  }

  & .ui.table tbody tr.active.deleted-treatment {
    color: red !important;
  }

  & .ui.table tbody tr.greyed-treatment {
    color: grey;
  }
`;

const StyledTableCellWithIcons = styled(Table.Cell)`
  padding-right: 0 !important;
  text-align: center !important;
`;

interface State {
  loadingInProgress: boolean,
  treatmentFormOpen: boolean,
  treatmentCostEstimateModalOpen: boolean,
  allMandates: Array<MandateDto>,
  cancelTokenSource: CancelTokenSource
}

interface Props extends MessageSourceProps,
    TreatmentsConsumerRenderProps,
    AuthConsumerRenderProps,
    PatientConsumerRenderProps {
  currentConsultations: Array<ConsultationDto>
}

class TreatmentsInfo extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      loadingInProgress: false,
      treatmentFormOpen: false,
      treatmentCostEstimateModalOpen: false,
      allMandates: [],
      cancelTokenSource
    }

    getAllMandates(cancelTokenSource)
        .then(response => {
          this.setState({
            allMandates: response
          })
        })
        .catch(noop)
  }

  getTreatmentsByPatientId(patientId: number): void {
    const {changeTreatments} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      loadingInProgress: true
    });

    const onFinally = () => {
      this.setState({
        loadingInProgress: false
      })
    };

    getAllTreatmentsByPatientId(cancelTokenSource, patientId)
        .then(response => changeTreatments(response))
        .catch(noop)
        .finally(onFinally)
  }

  componentDidMount(): void {
    this.mounted = true;
    const {currentPatient} = this.props;

    if (currentPatient) {
      this.getTreatmentsByPatientId(currentPatient.id);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<any>, snapshot?: any): void {
    if (!this.mounted) {
      return
    }
    const {currentPatient, changeTreatments} = this.props;

    if (prevProps.currentPatient != currentPatient) {
      if (currentPatient) {
        this.getTreatmentsByPatientId(currentPatient.id);
      } else {
        changeTreatments([]);
      }
    }
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  markTreatment = (treatmentId: number) => {
    const {currentTreatments, changeSelectedTreatment} = this.props;

    const foundTreatment = currentTreatments!
        .find((treatment: TreatmentDto) => treatment.id === treatmentId);

    changeSelectedTreatment(foundTreatment);
  };

  renderTreatmentInfoTable(): React.ReactNode {

    const {selectedTreatment, currentTreatments, getMessage, currentPatient, currentUser} = this.props;
    const {allMandates} = this.state;
    const selectedTreatmentId = selectedTreatment ? selectedTreatment.id : -1;

    return (
        <TreatmentsInfoContainer>
          <Table basic="very" celled padded selectable compact>
            <Table.Header>
              <Table.Row>
                <HomeTableHeaderCell>{getMessage('bill')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('date')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('tariff')}</HomeTableHeaderCell>
              </Table.Row>
            </Table.Header>
            {currentTreatments && (
                <Table.Body>
                  {
                    currentTreatments.map(treatment => {

                      const sameMandate = currentUser!.userConfig.mandateId === treatment.mandateId;
                      const mandate = allMandates.find(mandate => mandate.id === treatment.mandateId);
                      const iconTitle = mandate ? mandate.name : "";

                      const deletedTreatment = treatment.deleted === 1;

                      const greyed =
                          BillStatus.BILL == treatment.billStatus
                          || BillStatus.INTERMEDIATE_BILL == treatment.billStatus;

                      const shouldDisableEdit = !currentPatient
                          || (selectedTreatment
                              && BillStatus.OPEN != selectedTreatment.billStatus)
                          || (currentUser
                              && currentUser.userConfig.mandateId !== treatment.mandateId);

                      return (
                          <Table.Row
                              key={treatment.id}
                              onClick={() => this.markTreatment(treatment.id)}
                              active={treatment.id == selectedTreatmentId}
                              className={deletedTreatment ? "deleted-treatment" : greyed ? "greyed-treatment" : ""}
                              onDoubleClick={
                                !shouldDisableEdit
                                    ? () => this.props.getTreatment(TreatmentMode.EDIT)
                                    : () => this.props.getTreatment(TreatmentMode.DETAILS)
                              }
                          >
                            <StyledTableCellWithIcons>
                              {this.renderTreatmentBillStatus(treatment)}
                            </StyledTableCellWithIcons>
                            <Table.Cell>
                              <div className="date-class">
                                {
                                  moment(treatment.treatmentDate).format("DD.MM.YYYY")
                                }
                              </div>
                              <div className="icon-class">
                                {
                                  sameMandate ? <Icon
                                      name="circle"
                                      color="grey"
                                      title={iconTitle}
                                  /> : ""
                                }
                              </div>
                            </Table.Cell>
                            <Table.Cell>{treatment.tariff}</Table.Cell>
                          </Table.Row>
                      );
                    })
                  }
                </Table.Body>
            )}
          </Table>
        </TreatmentsInfoContainer>
    );
  }

  renderTreatmentsForm(): React.ReactNode {

    const {currentPatient, addTreatment, editTreatment, selectedTreatment, toggleUpsertModal, treatmentFormOpen,
      treatment, currentConsultations, changeTreatments} = this.props;
    const currentPatientId = currentPatient ? currentPatient.id : -1;

    return (
        treatmentFormOpen && (
            <TreatmentsForm
                treatmentFormOpen={treatmentFormOpen}
                toggleUpsertModal={toggleUpsertModal}
                currentPatientId={currentPatientId}
                currentConsultations={currentConsultations}
                treatment={treatment}
                addTreatment={addTreatment}
                editTreatment={editTreatment}
                selectedTreatment={selectedTreatment}
                changeTreatments={changeTreatments}
            />)
    );
  }

  render(): React.ReactNode {

    const {loadingInProgress} = this.state;
    const {getMessage} = this.props;

    if (loadingInProgress) {
      return (
          <LoaderComponent message={getMessage("treatments.loader")}/>
      );
    }

    return (
        <Fragment>
          {this.renderTreatmentInfoTable()}
          {this.renderTreatmentsForm()}
        </Fragment>
    );
  }

  renderTreatmentBillStatus = (treatment: TreatmentDto): React.ReactNode => {

    if (BillStatus.BILL == treatment.billStatus) {
      if (BillStatus.PAID_CLOSED == treatment.paymentStatus) {
        return <Icon.Group>
          <Icon name="file alternate outline" color={"green"}/>
          <Icon corner="bottom right" name="check" color={"green"} />
        </Icon.Group>;
      } else if (BillStatus.PAID_OPEN == treatment.paymentStatus) {
        return <Icon.Group>
          <Icon name="file alternate" color={"yellow"}/>
          <Icon corner="bottom right" name="check" color={"yellow"} />
        </Icon.Group>
      }
      return <Icon name={"file alternate outline"}/>;
    } else if (BillStatus.INTERMEDIATE_BILL == treatment.billStatus) {
      return <Icon name={"file alternate"}/>
    } else if (treatment.status) {
      return <Icon name={"key"}/>
    }

    return <Fragment/>
  }
}

export default withMessages(withAuthContext(TreatmentsInfo));
